/* eslint-disable */
/*
 * Copyright (c) 2022, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Typography, Box, useTheme } from '@material-ui/core';
import Link from 'components/ChoreoSystem/Link/Link';
import EmailLoginButton from 'modules/login/buttons/EmailLoginButton';
import GithubLoginButton from 'modules/login/buttons/GithubLoginButton';
import GoogleLoginButton from 'modules/login/buttons/GoogleLoginButton';
import AnonymousLoginButton from 'modules/login/buttons/AnonymousLoginButton';
import { Link as RouterLink } from 'react-router-dom';
import {
  disableAnonymousLogin,
  disableEnterpriseLogin,
  emailLoginConfigs,
  enableMicrosoftLogin,
} from '../../../config';
import EnterpriseLoginButton from '../buttons/EnterpriseLoginButton';
import EnterpriseLoginBox from '../EnterpriseLoginBox';
import MicrosoftLoginButton from '../buttons/MicrosoftLoginButton';
import OrDivider from 'components/ChoreoSystem/Authenticate/OrDivider/OrDivider';
import AuthenticationFormCard from 'components/ChoreoSystem/Authenticate/AuthenticationFormCard/AuthenticationFormCard';

interface LoginCardProps {
  isInvitation: boolean;
  isMobile: boolean;
  invitedOrgName: string;
  onEmailClick: () => void;
  onGithubClick: () => void;
  onGoogleClick: () => void;
  onMicrosoftClick: () => void;
  onAnonymousClick: () => void;
  onEnterpriseLoginClick: (userEmail: string) => void;
  /** Set by the auth dialog to switch to sign up in place of navigating away. */
  onSignUpClick?: () => void;
  /**
   * Set by the auth dialog so the modal can name itself after the card title
   * and move focus there when the card is swapped.
   */
  titleId?: string;
}

export default function LoginCard(props: LoginCardProps) {
  const {
    isInvitation,
    invitedOrgName,
    onEmailClick,
    onGithubClick,
    onGoogleClick,
    onMicrosoftClick,
    onAnonymousClick,
    onEnterpriseLoginClick,
    isMobile,
    onSignUpClick,
    titleId,
  } = props;
  const theme = useTheme();
  const [enableEnterpriseLogin, setEnableEnterpriseLogin] = useState(false);
  return (
    <AuthenticationFormCard
      orgName={invitedOrgName}
      isInvitation={isInvitation}
      testId="authenticate-form-card-login"
      isMobile={isMobile}
      isEmailLogin={emailLoginConfigs.ENABLE_EMAIL_LOGIN === 'true'}
      hideRegionSelector={isInvitation || enableEnterpriseLogin}
      onSignUpClick={onSignUpClick}
      titleId={titleId}
      title={
        !enableEnterpriseLogin ? (
          <FormattedMessage
            id="authentication.login.signIn.title"
            defaultMessage="Sign In"
          />
        ) : (
          <FormattedMessage
            id="authentication.login.signInWithEnterpriseId.title"
            defaultMessage="Sign in with Enterprise ID"
          />
        )
      }
    >
      <Box>
        {enableEnterpriseLogin && (
          <EnterpriseLoginBox
            handleEnterpriseLoginClick={onEnterpriseLoginClick}
            setEnableEnterpriseLogin={setEnableEnterpriseLogin}
          />
        )}
        {!enableEnterpriseLogin && (
          <Box>
            <Box mb={1}>
              <GoogleLoginButton onClick={onGoogleClick} />
            </Box>
            <Box mb={1}>
              <GithubLoginButton onClick={onGithubClick} />
            </Box>

            {enableMicrosoftLogin === 'true' && (
              <Box mb={1}>
                <MicrosoftLoginButton onClick={onMicrosoftClick} />
              </Box>
            )}
            {disableEnterpriseLogin !== 'true' && (
              <Box>
                <EnterpriseLoginButton
                  onClick={() => {
                    setEnableEnterpriseLogin(true);
                  }}
                />
              </Box>
            )}
            {emailLoginConfigs.ENABLE_EMAIL_LOGIN === 'true' && (
              <>
                <OrDivider
                  text={
                    <FormattedMessage
                      id="modules.login.loginCard.loginCard.orDivider"
                      defaultMessage="OR"
                    />
                  }
                />
                <Box>
                  <EmailLoginButton onClick={onEmailClick} />
                </Box>
              </>
            )}
          </Box>
        )}
        {!isInvitation &&
          !enableEnterpriseLogin &&
          disableAnonymousLogin === 'false' && (
            <AnonymousLoginButton onClick={onAnonymousClick} />
          )}
      </Box>
    </AuthenticationFormCard>
  );
}
